/**
 * @sazeo/sdk — Training & Certifications hooks
 *
 * TanStack Query wrappers for the training addon. All calls route through
 * the Edge Function at `/addons/training/*` guarded by
 * requireAddonEnabled() and enforced by RLS via the caller's JWT.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TrainingCourse {
  id: string;
  tenant_id: string;
  title: string;
  description: string | null;
  category:
    | 'onboarding'
    | 'compliance'
    | 'technical'
    | 'soft_skills'
    | 'leadership'
    | 'safety'
    | 'general';
  status: 'active' | 'archived' | 'draft';
  duration_hours: number | null;
  mandatory: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface TrainingEnrollment {
  id: string;
  tenant_id: string;
  course_id: string;
  user_id: string;
  status: 'enrolled' | 'in_progress' | 'completed' | 'failed';
  completed_at: string | null;
  score: number | null;
  created_at: string;
  updated_at: string;
  training_courses?: TrainingCourse;
}

export interface TrainingCourseCreateInput {
  title: string;
  description?: string | null;
  category?: TrainingCourse['category'];
  status?: TrainingCourse['status'];
  duration_hours?: number | null;
  mandatory?: boolean;
}

export interface TrainingCourseUpdateInput extends Partial<TrainingCourseCreateInput> {}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const trainingKeys = {
  all: ['training'] as const,
  courses: (tenantId: string) => [...trainingKeys.all, 'courses', tenantId] as const,
  myEnrollments: (tenantId: string) => [...trainingKeys.all, 'enrollments-mine', tenantId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers (same apiFetch pattern as contracts.ts)
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(buildFunctionUrl(path), {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useTrainingCourses(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: trainingKeys.courses(tenantId),
    queryFn: () => apiFetch<TrainingCourse[]>(supabase, 'addons/training/courses'),
    enabled: Boolean(tenantId),
  });
}

export function useMyEnrollments(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: trainingKeys.myEnrollments(tenantId),
    queryFn: () => apiFetch<TrainingEnrollment[]>(supabase, 'addons/training/enrollments/mine'),
    enabled: Boolean(tenantId),
  });
}

export function useCreateCourse(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  void tenantId;
  return useMutation({
    mutationFn: (input: TrainingCourseCreateInput) =>
      apiFetch<TrainingCourse>(supabase, 'addons/training/courses', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: trainingKeys.all });
    },
  });
}

export function useUpdateCourse(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  void tenantId;
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TrainingCourseUpdateInput }) =>
      apiFetch<TrainingCourse>(supabase, `addons/training/courses/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: trainingKeys.all });
    },
  });
}

export function useDeleteCourse(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  void tenantId;
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/training/courses/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: trainingKeys.all });
    },
  });
}

export function useEnrollInCourse(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (courseId: string) =>
      apiFetch<TrainingEnrollment>(supabase, 'addons/training/enrollments', {
        method: 'POST',
        body: JSON.stringify({ course_id: courseId }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: trainingKeys.myEnrollments(tenantId) });
    },
  });
}
